import { computed, ref, useStore } from "@nuxtjs/composition-api";

import { useDebounce } from "@/composables/utils";
import { useCreatorShopPostsStore, useCreatorShopSearchStore } from "@/store";
import { useCreatorProductStore } from "@/store/creator-products";
import { getExperimentsStore } from "@/store/experiments";
import { SEARCH_DEFAULT_PAGE_SIZE, SEARCH_FETCH_DEBOUNCE_DELAY } from "@/utils/constants";
import { FEATURE_FLAG_ACTIVE, FLAG_CREATOR_SHOP_SEARCH } from "@/utils/experiments/constants";

export const useCreatorShopRecentProducts = (profileId: string) => {
  if (!profileId) {
    throw new Error("useCreatorShopRecentProducts() requires a profileId be supplied.");
  }

  const store = useStore();
  const creatorShopSearchStore = useCreatorShopSearchStore();
  const experimentsStore = getExperimentsStore(store);
  const creatorProductStore = useCreatorProductStore();

  const searchPage = ref(1);
  const searchLoading = ref(false);
  const searchPaginating = ref(false);
  const searchError = ref(false);

  const isCreatorShopSearchEnabled = computed(
    () => experimentsStore.flag(FLAG_CREATOR_SHOP_SEARCH)?.value === FEATURE_FLAG_ACTIVE,
  );

  const showLoading = computed(() => {
    if (isCreatorShopSearchEnabled.value) {
      return searchLoading.value;
    }
    return Boolean(
      creatorProductStore.recentProductsFetchState.pending ||
        creatorProductStore.recentProductsSearchFetchState.pending,
    );
  });

  const showError = computed(() => {
    if (isCreatorShopSearchEnabled.value) {
      return searchError.value;
    }
    return Boolean(
      creatorProductStore.recentProductsFetchState.error || creatorProductStore.recentProductsSearchFetchState.error,
    );
  });

  const legacyProductSearchFeed = computed(() => creatorProductStore.recentProductsSearchFeed);
  const legacyProductFeed = computed(() => creatorProductStore.recentProductsFeed);
  const productSearchFeed = computed(() => creatorShopSearchStore.productsSearchFeedMap);
  const productFeed = computed(() => creatorShopSearchStore.productsFeedMap);

  const hasMoreProducts = (query = "") => {
    let productsFeed = creatorShopSearchStore.productsFeedMap[profileId];
    if (query) {
      productsFeed = creatorShopSearchStore.productsSearchFeedMap[query];
    }

    if (!productsFeed) {
      return false;
    }
    const productsTotal = productsFeed.total;
    const productsTotalPages = Math.ceil(productsTotal / SEARCH_DEFAULT_PAGE_SIZE);
    const productsComplete = productsFeed.complete;
    const productsCurrentPage = searchPage.value;
    if (!productsComplete) {
      return true;
    }
    return productsCurrentPage < productsTotalPages;
  };

  const onFetchMoreProducts = async (query = "") => {
    if (searchLoading.value || searchPaginating.value) {
      return;
    }
    try {
      searchPage.value += 1; // increment the page for the next fetch
      searchPaginating.value = true; // set paginating state to true
      if (isCreatorShopSearchEnabled.value) {
        if (!hasMoreProducts(query)) {
          return; // do not paginate if no more products
        }
        await creatorShopSearchStore.fetchMoreProductSearchResults(profileId, {
          query,
          page: searchPage.value,
          limit: SEARCH_DEFAULT_PAGE_SIZE,
          aggregated: false,
        });
      } else if (query) {
        await creatorProductStore.searchCreatorRecentProducts(profileId, query);
      } else {
        await creatorProductStore.fetchCreatorRecentProducts(profileId);
      }
    } catch {
      searchError.value = true; // set error state if the fetch fails
    }
    searchPaginating.value = false; // reset paginating state
  };

  const onFetchProducts = async (query = "") => {
    try {
      searchError.value = false;
      searchPage.value = 1; // reset the page to 1 for the new search term
      searchLoading.value = true; // set loading to true while we fetch
      if (isCreatorShopSearchEnabled.value) {
        await creatorShopSearchStore.fetchProductSearchResults(profileId, {
          query,
          page: searchPage.value,
          limit: SEARCH_DEFAULT_PAGE_SIZE,
          aggregated: false,
        });
      } else if (query) {
        await creatorProductStore.searchCreatorRecentProducts(profileId, query);
      } else {
        await creatorProductStore.fetchCreatorRecentProducts(profileId);
      }
    } catch {
      searchError.value = true; // set error state if the fetch fails
    }
    searchLoading.value = false; // reset loading state
  };

  const debouncedOnFetchProducts = useDebounce(onFetchMoreProducts, SEARCH_FETCH_DEBOUNCE_DELAY);

  const resetPagination = () => {
    searchPage.value = 1;
    searchLoading.value = false;
    searchPaginating.value = false;
    searchError.value = false;
  };

  const resetSearch = () => {
    creatorProductStore.recentProductsSearchFeed = {};
    creatorShopSearchStore.resetSearch();
  };

  return {
    legacyProductSearchFeed,
    legacyProductFeed,
    productSearchFeed,
    productFeed,
    showLoading,
    showError,
    onFetchProducts,
    onFetchMoreProducts: debouncedOnFetchProducts,
    resetPagination,
    resetSearch,
  };
};

export const useCreatorShopRecentPosts = (profileId: string) => {
  if (!profileId) {
    throw new Error("useCreatorShopRecentPosts() requires a profileId be supplied.");
  }

  const store = useStore();
  const creatorShopSearchStore = useCreatorShopSearchStore();
  const experimentsStore = getExperimentsStore(store);
  const creatorShopPostsStore = useCreatorShopPostsStore();

  const searchPage = ref(1);
  const searchLoading = ref(false);
  const searchPaginating = ref(false);
  const searchError = ref(false);

  const isCreatorShopSearchEnabled = computed(
    () => experimentsStore.flag(FLAG_CREATOR_SHOP_SEARCH)?.value === FEATURE_FLAG_ACTIVE,
  );

  const showLoading = computed(() => {
    if (isCreatorShopSearchEnabled.value) {
      return searchLoading.value;
    }
    return creatorShopPostsStore.fetchState.pending;
  });

  const showError = computed(() => {
    if (isCreatorShopSearchEnabled.value) {
      return searchError.value;
    }
    return Boolean(creatorShopPostsStore.fetchState.error);
  });

  const legacyLtksSearchFeed = computed(() => creatorShopPostsStore._searchFeeds);
  const legacyLtksFeed = computed(() => creatorShopPostsStore._shopFeeds[profileId]);
  const ltksSearchFeed = computed(() => creatorShopSearchStore.ltksSearchFeedMap);
  const ltksFeed = computed(() => creatorShopSearchStore.ltksFeedMap[profileId]);

  const hasMorePosts = (query = "") => {
    let postsFeed = creatorShopSearchStore.ltksFeedMap[profileId];
    if (query) {
      postsFeed = creatorShopSearchStore.ltksSearchFeedMap[query];
    }

    if (!postsFeed) {
      return false;
    }

    const ltksTotal = postsFeed.total;
    const ltksTotalPages = Math.ceil(ltksTotal / SEARCH_DEFAULT_PAGE_SIZE);
    const ltksComplete = postsFeed.complete;
    const ltksCurrentPage = searchPage.value;
    if (!ltksComplete) {
      return true;
    }
    return ltksCurrentPage < ltksTotalPages;
  };

  const onFetchMorePosts = async (query = "") => {
    if (searchLoading.value || searchPaginating.value) {
      return;
    }
    try {
      searchPage.value += 1; // increment the page for the next fetch
      searchPaginating.value = true; // set paginating state to true

      if (isCreatorShopSearchEnabled.value) {
        if (!hasMorePosts(query)) {
          return; // do not paginate if no more posts
        }
        await creatorShopSearchStore.fetchMoreLtkSearchResults(profileId, {
          query,
          page: searchPage.value,
          limit: SEARCH_DEFAULT_PAGE_SIZE,
          ...(query
            ? {
                aggregated: true,
                types: ["post", "product"],
              }
            : {
                aggregated: false,
                types: ["post"],
              }),
        });
      } else if (query) {
        await creatorShopPostsStore.fetchBySearchTerm(profileId, query);
      } else {
        await creatorShopPostsStore.fetch(profileId);
      }
    } catch {
      searchError.value = true; // set error state if the fetch fails
    }
    searchPaginating.value = false; // reset paginating state
  };

  const onFetchPosts = async (query = "") => {
    try {
      searchError.value = false;
      searchPage.value = 1; // reset the page to 1 for the new search term
      searchLoading.value = true; // set loading to true while we fetch
      if (isCreatorShopSearchEnabled.value) {
        await creatorShopSearchStore.fetchLtkSearchResults(profileId, {
          query,
          page: searchPage.value,
          limit: SEARCH_DEFAULT_PAGE_SIZE,
          ...(query
            ? {
                aggregated: true,
                types: ["post", "product"],
              }
            : {
                aggregated: false,
                types: ["post"],
              }),
        });
      } else if (query) {
        await creatorShopPostsStore.fetchBySearchTerm(profileId, query);
      } else {
        await creatorShopPostsStore.fetch(profileId);
      }
    } catch {
      searchError.value = true; // set error state if the fetch fails
    }
    searchLoading.value = false; // reset loading state
  };

  const debouncedOnFetchPosts = useDebounce(onFetchMorePosts, SEARCH_FETCH_DEBOUNCE_DELAY);

  const resetPagination = () => {
    searchPage.value = 1;
    searchLoading.value = false;
    searchPaginating.value = false;
    searchError.value = false;
  };

  const resetSearch = () => {
    creatorShopPostsStore.$reset();
    creatorShopSearchStore.resetSearch();
  };

  return {
    legacyLtksSearchFeed,
    legacyLtksFeed,
    ltksSearchFeed,
    ltksFeed,
    showLoading,
    showError,
    onFetchPosts,
    onFetchMorePosts: debouncedOnFetchPosts,
    resetPagination,
    resetSearch,
  };
};
